/**
 * Application Settings page — Admin only.
 *
 * Single consolidated page with collapsible category sections.
 * Each setting shows current value, default value, description,
 * last modified metadata. Inline edit with save. Reset to Default button.
 *
 * Requirements: 56.1, 56.2, 56.14, 56.15, 56.18, 56.19, 56.20
 */

import { useState, useEffect, useCallback } from 'react';
import {
  adminService,
  SettingsByCategory,
  ApplicationSetting,
} from '../../services/adminService';

export function AppSettings() {
  const [categories, setCategories] = useState<SettingsByCategory[]>([]);
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [editValue, setEditValue] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadSettings = useCallback(async () => {
    try {
      setLoading(true);
      const data = await adminService.getSettings();
      setCategories(data);
      // Expand all categories by default
      setExpandedCategories(new Set(data.map((c) => c.category)));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load settings.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  const toggleCategory = (category: string) => {
    setExpandedCategories((prev) => {
      const next = new Set(prev);
      if (next.has(category)) next.delete(category);
      else next.add(category);
      return next;
    });
  };

  const startEdit = (setting: ApplicationSetting) => {
    setEditingKey(setting.settingKey);
    setEditValue(setting.currentValue);
  };

  const cancelEdit = () => {
    setEditingKey(null);
    setEditValue('');
  };

  const saveSetting = async (key: string) => {
    try {
      setSaving(true);
      await adminService.updateSetting(key, editValue);
      setEditingKey(null);
      await loadSettings();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save setting.');
    } finally {
      setSaving(false);
    }
  };

  const resetToDefault = async (key: string) => {
    try {
      setSaving(true);
      await adminService.resetSetting(key);
      await loadSettings();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to reset setting.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div >
        <h1 className="text-lg font-semibold text-text-primary mb-6">Application Settings</h1>
        <p>Loading settings...</p>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-5xl">
      <h1 className="text-lg font-semibold text-text-primary mb-2">Application Settings</h1>
      <p className="text-sm text-slate-500 mb-6">
        Manage application configuration across all categories. Changes are recorded in the audit log.
      </p>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl px-6 py-4 text-red-700 text-sm mb-5" role="alert">
          {error}
          <button className="ml-4 underline" onClick={() => setError(null)}>Dismiss</button>
        </div>
      )}

      {categories.map((cat) => (
        <div key={cat.category} className="mb-4 bg-white rounded-lg border border-border">
          <button
            className="w-full flex items-center justify-between p-4 bg-page hover:bg-page/80 rounded-t-xl text-left font-medium"
            onClick={() => toggleCategory(cat.category)}
            aria-expanded={expandedCategories.has(cat.category)}
          >
            <span>{cat.category}</span>
            <span className="text-slate-400">
              {expandedCategories.has(cat.category) ? '▼' : '▶'} ({cat.settings.length})
            </span>
          </button>

          {expandedCategories.has(cat.category) && (
            <div className="divide-y">
              {cat.settings.map((setting) => (
                <div key={setting.settingKey} className="p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="font-medium">{setting.displayName}</div>
                      {setting.description && (
                        <div className="text-sm text-slate-500 mt-1">{setting.description}</div>
                      )}
                    </div>
                    <div className="flex gap-2 ml-4">
                      {setting.currentValue !== setting.defaultValue && (
                        <button
                          className="text-sm text-baker-red hover:underline"
                          onClick={() => resetToDefault(setting.settingKey)}
                          disabled={saving}
                        >
                          Reset to Default
                        </button>
                      )}
                    </div>
                  </div>

                  <div className="mt-2 flex items-center gap-4">
                    {editingKey === setting.settingKey ? (
                      <div className="flex items-center gap-2 flex-1">
                        <input
                          type="text"
                          value={editValue}
                          onChange={(e) => setEditValue(e.target.value)}
                          className="py-2 px-3 rounded-lg border border-border text-sm focus:outline-none focus:ring-2 focus:ring-baker-accent/40 flex-1"
                          aria-label={`Edit ${setting.displayName}`}
                        />
                        <button
                          className="px-3 py-1 bg-baker-red text-white rounded-lg text-sm font-medium hover:bg-baker-red/90"
                          onClick={() => saveSetting(setting.settingKey)}
                          disabled={saving}
                        >
                          Save
                        </button>
                        <button
                          className="px-3 py-1 rounded-lg border border-border bg-white text-sm text-slate-600 hover:bg-page"
                          onClick={cancelEdit}
                          disabled={saving}
                        >
                          Cancel
                        </button>
                      </div>
                    ) : (
                      <div className="flex items-center gap-4 flex-1">
                        <span className="font-mono text-sm bg-page px-3 py-1.5 rounded-lg">
                          {setting.currentValue}
                        </span>
                        <button
                          className="text-sm text-baker-red hover:underline"
                          onClick={() => startEdit(setting)}
                        >
                          Edit
                        </button>
                      </div>
                    )}
                  </div>

                  <div className="mt-1 text-xs text-slate-400 flex gap-4">
                    <span>Default: {setting.defaultValue}</span>
                    <span>Type: {setting.valueType}</span>
                    {setting.lastModifiedBy && (
                      <span>
                        Modified by {setting.lastModifiedBy}
                        {setting.lastModifiedAt && ` on ${new Date(setting.lastModifiedAt).toLocaleDateString()}`}
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      ))}

      {categories.length === 0 && (
        <p className="text-slate-400">No settings found. Settings will be populated during initial setup.</p>
      )}
    </div>
  );
}
